import type { AuthProviderType } from "@/lib/user/types";
import { baseUrl } from "@/services/api";
import { MethodType } from "@/types/all.types";

export const ENDPOINTS = {
  GET_USER_CHURNKEY: () => `${baseUrl}/user/churnkey`,
  REGISTER: (via_signature = false) => `/user/auth/register?via-signature=${via_signature ? 1 : 0}`,
  CHECK_REGISTER: () => `/user/auth/register-check`,
  LOGIN: (via_signature = false) =>
    `${baseUrl}/user/auth/login?via-signature=${via_signature ? 1 : 0}`,
  GET_AUTO_LOGIN: (id: string, hash: string, res: string) =>
    `${baseUrl}/user/auth/login/auto/${id}/${hash}?${res}`,
  GET_VERIFY_EMAIL: (id: string, hash: string, expires: string, signature: string) =>
    `${baseUrl}/user/auth/email/verify/${id}/${hash}?expires=${expires}&signature=${signature}`,
  LOGOUT: () => `/user/auth/logout`,
  GET_GOOGLE_REDIRECT: () => `${baseUrl}/user/auth/social/google/redirect/register`,
  GET_FACEBOOK_REDIRECT: () => `${baseUrl}/user/auth/social/facebook/redirect`,
  POST_PROVIDER_AUTH: (auth_intent: string, provider: AuthProviderType) =>
    `/user/auth/${provider}/${auth_intent}`,
  DELETE_PROFILE: () => `/user/profile/delete`,
  RESET_PASSWORD: () => `${baseUrl}/user/auth/reset/password`,
  RESET_NOTIFICATION: () => `/user/auth/reset`,
  VERIFY_EMAIL: () => `${baseUrl}/user/auth/email/resend-verify`,
  GET_FAQS: () => `${baseUrl}/common/faqs`,
  GET_BLOGS: (page: number = 20) => `${baseUrl}/common/blog?perPage=${page}`,
  GET_BLOG_BY_SLUG: (slug: string) => `${baseUrl}/common/blog/${slug}`,
  GET_BLOG_AUTHOR: (id: string) => `${baseUrl}/common/author/${id}`,
  GET_AUTHOR_INFO: (id: number) => `${baseUrl}/common/blog?author_id=${id}`,
  GET_TYPE_RELATED: () => `${baseUrl}/common/blog/type-related`,
  GET_BILLING: () => `user/billing`,
  GET_USER_MENAGEMENT: () => `${baseUrl}/user/managers`,
  UPDATE_ACCOUNT: () => `/user/profile`,
  CHANGE_PASSWORD: () => `/user/profile/password`,
  CHANGE_EMAIL: () => `/user/profile/email`,
  UPDATE_PREFERENCES: () => `user/profile/preferences`,
  UPDATE_PAYMENT: () => `${baseUrl}/user/subscription/update-payment`,
  CANCEL_SUBSCRIPTION: () => `${baseUrl}/user/billing/before-cancel`,
  POST_REACTIVATE_WITH_DISCOUNT: () => `${baseUrl}/user/billing/reactivate?applyDiscount=true`,
  GET_REACTIVATE_SUBSSCRIPTION: () => `${baseUrl}/user/billing/reactivate`,
  CREATE_PAYMENT_METHODS: () => `${baseUrl}/user/payment-methods`,
  GET_STICKERS: () => `/editor/design/stickers`,
  GET_STYLES: () => `/editor/design/body-types`,
  GET_PATTERNS: () => `/editor/design/patterns`,
  GET_MAKE_BORDERS: () => `/editor/design/markers`,
  GET_LOGO: () => `${baseUrl}/editor/brand-logos`,
  RESET_LOGO: () => `${baseUrl}/editor/brand-logos?reset=true`,
  UPDATE_LOGO: () => `${baseUrl}/editor/brand-logos`,
  DELETE_LOGO: (uuid: string) => `${baseUrl}/editor/brand-logos/${uuid}`,
  VISITOR_INITIALIZE: () => `/guest`,
  PUT_INSERT_HOTJR_ID: () => `/guest/set-analytic-id`,
  GET_PLAN_DETAILS: (uuid: string) => `${baseUrl}/user/plans/${uuid}`,
  POST_SCREENSHOT: () => `/editor/screenshot`,
  POST_CONTACT: () => `${baseUrl}/common/contact`,
  POST_UPLOAD_FILE: () => `${baseUrl}/editor/files`,
  PUT_UPLOAD_FILE_METADATA: (id: number) => `${baseUrl}/editor/files/${id}`,
  GET_TIMEZONE: () => `${baseUrl}/common/timezones`,
  GET_COUNTRIES: () => `${baseUrl}/common/countries`,
  POST_NOTIFICATION: (id: string) => `${baseUrl}/user/profile/banner-alerts/${id}`,
  GET_NOTIFICATION: () => `${baseUrl}/user/profile/banner-alerts`,
  DELETE_UPLOAD_FILE: (id: number) => `${baseUrl}/editor/files/${id}`,
  POST_UPLOAD_MULTI_FILE: () => `${baseUrl}/editor/files`,
  GET_PRICING: () => `${baseUrl}/user/plans`,
  POST_CHANGE_INVOICE_EMAIL: () => `${baseUrl}/user/billing/email`,
  GET_USER_BILLING: () => `${baseUrl}/user/billing`,
  GET_USER_BILLING_RECEIPTS: () => `${baseUrl}/user/billing/receipts`,
  VALIDATE_VAT: () => `${baseUrl}/user/billing/tax`,
  VALIDATE_POSTAL_CODE: (planUuid: string) =>
    `${baseUrl}/user/billing/validate-postal-code/${planUuid}`,
  SHOW_USER_PLAN: ({
    uuid,
    isCompany,
    state,
    country,
    withPostalCode,
    postalCode,
  }: {
    uuid: string;
    isCompany: boolean | null;
    state?: string;
    country?: string;
    withPostalCode?: boolean;
    postalCode?: string;
  }) => {
    if(!uuid) throw new Error('UUID is required');

    let url = `${baseUrl}/user/plans/${uuid}/invoice?is_company=${isCompany}&with-billing-details=true`;

    if (country) {
      url += `&country=${country}`;
    }

    if (withPostalCode) {
      url += `&handle_tax=1`;
    }

    if (state) {
      url += `&state=${state}`;
    }

    if (postalCode) {
      url += `&postal_code=${postalCode}`;
    }

    return url;
  },

  SHOW_USER_PLANS: (uuid: string) => `${baseUrl}/user/plans/${uuid}?preview-invoice=true`,
  POST_SUBSCRIBE: (uuid: string, bypassPreCondition: boolean) =>
    `${baseUrl}/user/plans/${uuid}?bypassPreCondition=${bypassPreCondition ? 1 : 0}`,
  POST_SUBSCRIBE_FREEMIUM: (uuid: string) => `${baseUrl}/user/plans/${uuid}`,
  ACTIVATE_SELECT_FROM_LIST: () => `/user/qr-codes/bulk/activate`,
  CREATE_FOLDER: () => `/user/qr-folders`,
  UPDATE_FOLDER: (id: number) => `user/qr-folders/${id}`,
  MOVE_TO_FOLDER: (qr_id: number, folder_id: number) =>
    `user/qr-codes/${qr_id}/move-to-folder/${folder_id}`,
  GET_QR_LIST: () => `/user/qr-codes`,
  DELETE_QR_LIST: (params: unknown) => `/user/qr-codes/${params}`,
  GET_QR_FOLDERS: () => `/user/qr-folders`,
  SELECT_DELETE: () => `user/qr-codes/bulk/delete`,
  MOVE_TO_FOLDER_SELECT: (id: number) => `user/qr-codes/bulk/move-to-folder/${id}`,
  DUBLICATE_QR: (id: number) => `/user/qr-codes/${id}/duplicate`,
  BULK_DOWNLOAD: (folder_id: number, type: string) =>
    `user/qr-folders/${folder_id}/download?format=${type}`,
  BULK_DOWNLOAD_FROM_SELECT: (format: string) => `/user/qr-codes/bulk/download?format=${format}`,
  DELETE_FOLDER: (params: unknown) => `/user/qr-folders/${params}`,
  REMOVE_FROM_FOLDER: () => `/user/qr-codes/remove-folder`,
  SHOW_QR_CODE: (params: unknown) => `/user/qr-codes/${params}`,
  GET_ANALYTICS_SHOW: (params: unknown) => `/user/qr-codes/${params}?with-detail=1`,
  RESET_ANALYTICS: (id: string) => `/user/qr-codes/${id}/statistics/reset`,
  ANALYTICS_STATISTICS: (params: unknown) => `/user/qr-codes/${params}/statistics`,
  UPDATE_DETAIL: (params: unknown) => `qr-code/${params}?_method=PATCH&detail_only=1`,
  POST_SAVED_DESIGN: (id: number) => `${baseUrl}/user/qr-codes/${id}/save-design`,
  DELETE_SAVED_DESIGN: (id: number) => `${baseUrl}/user/qr-saved-designs/${id}`,
  GET_SAVED_DESIGN: () => `${baseUrl}/user/qr-saved-designs`,
  GET_LAST_SCANS: (params: unknown) => `${baseUrl}/user/qr-codes/${params}/last-scans`,
  UPDATE_USER_MENAGEMENT: (params: unknown) => `${baseUrl}/user/managers/${params}`,
  DELETE_USER_MENAGEMENT: (params: unknown) => `${baseUrl}/user/managers/${params}`,
  DOWNLOAD_PREVIEW_RESULT: (id: number, type: string | null) =>
    `${baseUrl}/user/qr-codes/${id}/download?format=${type}`,
  DOWNLOAD_CSV_TYPE: (type: string) =>
    `${baseUrl}/user/qr-codes/bulk/download-example-csv?type=${type}`,
  DOWNLOAD_STATISTIC_CSV_FORMAT: (id: string) =>
    `${baseUrl}/user/qr-codes/${id}/statistics/download`,
  GET_USER_PROFILE: () => `${baseUrl}/user/profile/show?with=usage,permissions,sidebar-message`,
  POST_PASSWORD_RECOVERY: () => `${baseUrl}/user/auth/reset/token-verify`,

  GET_PASSWORD_RECOVERY: () =>
    `${baseUrl}/user/profile/show?with=usage,permissions,sidebar-message&permissions=generate,generateDynamic,generateStatic`,
  GET_API_TOKEN: () => `${baseUrl}/user/api-tokens`,
  POST_QR_SCAN: (slug: string) => `${baseUrl}/qr-code/${slug}/scan`,
  POST_VALIDATE_SOCIAL_TOKEN: (method: MethodType, provider: string | null) =>
    `${baseUrl}/user/auth/social/${provider}/callback/${method}`,
  GET_MATERIALS: () => `${baseUrl}/common/marketing-materials`,
  APPLY_DISCOUNT: () => `${baseUrl}/user/plans/apply-discount`,
  RETRY_PAYMENT: () => `/user/billing/retry-charge`,
};
